import { Component, computed, input } from "@angular/core";
import { EChartsOption } from "echarts/types/dist/shared";

const TRACK_COLORS = ["#1890ff", "#fa8c16", "#52c41a", "#722ed1", "#eb2f96"];
/** Height (px) of one stacked track and the space around them. */
const TRACK_HEIGHT = 90;
const TRACK_GAP = 22;
const TOP = 28;
const BOTTOM = 50;

export interface HeaderTrackSeries {
  name: string;
  label: string;
  values: number[];
}

/**
 * Header values against the trace number as stacked tracks — one small plot
 * per field, sharing one trace axis (crosshair and zoom are linked), so how
 * several headers change along the same traces reads at a glance.
 */
@Component({
  selector: "app-segy-header-tracks",
  template: `
    @if (options(); as opts) {
      <div echarts class="tracks-chart" [style.height.px]="height()" [options]="opts"></div>
    }
  `,
  styles: [":host { display: block; } .tracks-chart { width: 100%; }"],
  standalone: false,
})
export class SegyHeaderTracksComponent {
  /** 1-based trace numbers (the shared x axis). */
  public readonly traces = input<number[]>([]);
  public readonly series = input<HeaderTrackSeries[]>([]);

  protected readonly height = computed(
    () => TOP + BOTTOM + this.series().length * (TRACK_HEIGHT + TRACK_GAP),
  );

  protected readonly options = computed<EChartsOption | null>(() => {
    const traces = this.traces();
    const series = this.series();
    if (!traces.length || !series.length) return null;
    const indices = series.map((_, i) => i);
    const last = series.length - 1;

    return {
      animation: false,
      axisPointer: { link: [{ xAxisIndex: "all" }] },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "line" },
        formatter: (params: any) => {
          const list = Array.isArray(params) ? params : [params];
          if (!list.length) return "";
          const rows = list
            .map((p: any) => `${p.marker}${p.seriesName}: <b>${p.value[1]}</b>`)
            .join("<br/>");
          return `trace ${list[0].value[0]}<br/>${rows}`;
        },
      },
      grid: series.map((_, i) => ({
        left: 70,
        right: 20,
        top: TOP + i * (TRACK_HEIGHT + TRACK_GAP),
        height: TRACK_HEIGHT,
      })),
      xAxis: series.map((_, i) => ({
        type: "value",
        gridIndex: i,
        min: "dataMin",
        max: "dataMax",
        axisLabel: { show: i === last },
        name: i === last ? "trace" : "",
        nameLocation: "middle",
        nameGap: 26,
      })),
      yAxis: series.map((s, i) => ({
        type: "value",
        gridIndex: i,
        scale: true,
        name: s.label,
        nameLocation: "end",
        nameGap: 4,
        nameTextStyle: {
          align: "left",
          fontWeight: "bold",
          color: TRACK_COLORS[i % TRACK_COLORS.length],
        },
        splitNumber: 3,
        axisLabel: { fontSize: 10 },
      })),
      dataZoom: [
        { type: "inside", xAxisIndex: indices, filterMode: "none" },
        {
          type: "slider",
          xAxisIndex: indices,
          height: 16,
          bottom: 4,
          filterMode: "none",
        },
      ],
      series: series.map((s, i) => ({
        name: s.label,
        type: "line",
        xAxisIndex: i,
        yAxisIndex: i,
        showSymbol: traces.length <= 300,
        symbolSize: 4,
        color: TRACK_COLORS[i % TRACK_COLORS.length],
        lineStyle: { width: 1.4 },
        data: traces.map((t, j) => [t, s.values[j]]),
      })),
    } as EChartsOption;
  });
}
